import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AttendanceGrid, { AddGuestTile, ReportSubmitBar } from '../../components/AttendanceGrid';
import API from '../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    getReportDetailsRequested,
    getReportsRequested,
    updateAttendance,
    updateGuestAttendance,
    updateTestimonies,
    updateEvaluations,
    updateDate,
    addReportGuest,
    submitNewReportRequested
} from "../../actions/reportAction";
import { connect } from "react-redux";
import { ArrowLeftIcon, ClipboardListIcon, UserGroupIcon, AnnotationIcon } from '@heroicons/react/outline';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

/**
 * Renders a form for adding a new report, including fields for cell members, guests, testimonies, evaluations, and date.
 * @param {Object} props - The component props.
 * @param {Array} props.cellMembers - An array of cell members.
 * @param {Array} props.guests - An array of guests.
 * @param {Object} props.report - The report object.
 * @param {boolean} props.reportDetailsRequested - A boolean indicating whether report details have been requested.
 * @param {Function} props.updateAttendance - A function to update attendance.
 * @param {Function} props.updateGuestAttendance - A function to update guest attendance.
 * @param {Function} props.updateTestimonies - A function to update testimonies.
 * @param {Function} props.updateEvaluations - A function to update evaluations.
 * @param {Function} props.updateDate - A function to update the report date.
 * @param {Function} props.submitNewReportRequested - A function to submit a new report.
 * @param {Function} props.getReportDetailsRequested - A function to get report details.
 * @returns {JSX.Element} - The rendered component.
 */
const AddReport = ({
    saveStatus,
    saveError,
    cellMembers,
    guests,
    report,
    reportDetailsRequested,
    updateAttendance,
    updateGuestAttendance,
    updateTestimonies,
    updateEvaluations,
    updateDate,
    addReportGuest: addGuestToReport,
    getReportsRequested: getReport,
    submitNewReportRequested: submitReport,
    getReportDetailsRequested: reportGetDetails,
}) => {
    // const [cellId, setCellId] = React.useState(null);
    // const location = useLocation(); 
    const navigate = useNavigate();
    const [errors, setErrors] = React.useState({});
    const [formIsValid, setFormIsValid] = React.useState(false);
    // const [loading, setLoading] = React.useState(false);
    // const [report, setReport] = React.useState(null);
    // const [testimonies, setTestimonies] = React.useState("");
    // const [evaluations, setEvaluations] = React.useState("");
    // const [cellMembers, setCellMembers] = React.useState([]);
    // const [guests, setGuests] = React.useState([]);
    // const [date, setDate] = React.useState("");
    // const dispatch = useDispatch();
    const { cellId } = useParams();

    React.useEffect(() => {
        const isValid = handleValidaton();
        setFormIsValid(isValid);
    }, [report]);

    // Leave the page only after the report and its attendance are saved;
    // on failure stay here so nothing that was entered is lost.
    const submittedRef = React.useRef(false); // only react to a save started on this page
    React.useEffect(() => {
        if (!submittedRef.current) return;
        if (saveStatus === 'saved') {
            submittedRef.current = false;
            notify("Report created successfully!");
            getReport(cellId);
            navigate('/dashboard/reports');
        } else if (saveStatus === 'failed') {
            submittedRef.current = false;
            toast.error(saveError || 'The report could not be saved. Try again.');
        }
    }, [saveStatus]);

    React.useEffect(() => {
        if (!reportDetailsRequested) {
            reportGetDetails({ cellId });
        }
    }, [reportDetailsRequested]);

    const notify = (msg) => toast.success(msg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
    });

    const memberPeople = cellMembers
        .map((m) => ({ id: m.id || m.member_id, name: m.name, present: m.attendance === true }))
        .filter((p) => p.id != null);
    const guestPeople = (guests || [])
        .map((g) => ({ id: g.id || g.guest_id, name: g.name, present: g.attendance === true }))
        .filter((p) => p.id != null);

    const setAllMembers = (value) => {
        memberPeople.forEach((p) => updateAttendance({ id: p.id, value }));
    };
    const setAllGuests = (value) => {
        guestPeople.forEach((p) => updateGuestAttendance({ id: p.id, value }));
    };

    // Saves the visitor as a guest of this cell, then marks them present.
    const handleAddGuest = async ({ name, phoneNumber }) => {
        const created = await API.postData(`cells/${cellId}/guests`, { name, phoneNumber });
        if (!created || created.id == null) throw new Error('Could not add guest. Try again.');
        addGuestToReport({ ...created, attendance: true });
    };

    const handleTmChange = (e) => {
        updateTestimonies(e.target.value);
    }

    const handleEvChange = (e) => {
        updateEvaluations(e.target.value);
    }

    const handleDateChange = (e) => {
        updateDate(e.target.value);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        submittedRef.current = true;
        submitReport({
            cell_id: cellId,
            cellMemberAttendance: cellMembers,
            guestAttendance: guests,
            evaluations: report.evaluations,
            testimonies: report.testimonies,
            date: report.date,
        })
        // Success/failure is handled in the effect above, once the server has answered.
    }

    const handleValidaton = () => {
        let errors = {};
        let formIsValid = true;
        if (report != null && (report.date == null || report.date === "")) {
            formIsValid = false;
            errors["date"] = "Date cannot be empty";
        }

        setErrors(errors);
        return formIsValid;
    }

    // const data = React.useMemo(() => cellMembers, [])

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900">
            <ToastContainer />
            {report && (
                <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                                type="button"
                            >
                                <ArrowLeftIcon className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Create Report</h1>
                                <p className="text-gray-500 mt-1">Record attendance, testimonies, and evaluations.</p>
                            </div>
                        </div>
                        <div className="bg-white shadow rounded-lg p-3 w-full sm:w-auto">
                            <label htmlFor="day" className="block text-sm font-medium text-gray-700">Report Date</label>
                            <input
                                className="mt-2 px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                autoComplete="Date"
                                required
                                name="date"
                                type="date"
                                value={report.date || ""}
                                onChange={handleDateChange}
                            />
                            {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
                        </div>
                    </div>

                    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                        <AttendanceGrid
                            title="Cell Members"
                            icon={<UserGroupIcon className="h-5 w-5 text-emerald-600" />}
                            people={memberPeople}
                            onToggle={(id, value) => updateAttendance({ id, value })}
                            onSetAll={setAllMembers}
                            emptyText="No cell members yet. Add them on the Cell Members page."
                        />

                        <AttendanceGrid
                            title="Guests"
                            icon={<ClipboardListIcon className="h-5 w-5 text-emerald-600" />}
                            people={guestPeople}
                            onToggle={(id, value) => updateGuestAttendance({ id, value })}
                            onSetAll={guestPeople.length > 1 ? setAllGuests : null}
                            emptyText="No guests yet. Add a first-time visitor below."
                        >
                            <AddGuestTile onAdd={handleAddGuest} />
                        </AttendanceGrid>

                        <section className="bg-white shadow-lg rounded-lg p-6">
                            <div className="flex items-center gap-2">
                                <AnnotationIcon className="h-5 w-5 text-emerald-600" />
                                <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Capture testimonies and evaluation notes for this meeting.</p>

                            <div className="mt-6">
                                <label htmlFor="testimonies" className="block text-sm font-medium text-gray-700">Testimonies</label>
                                <textarea
                                    id="testimonies"
                                    name="testimonies"
                                    rows={5}
                                    className="mt-2 px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                    value={report.testimonies || ""}
                                    onChange={handleTmChange}
                                />
                            </div>

                            <div className="mt-6">
                                <label htmlFor="evaluations" className="block text-sm font-medium text-gray-700">Evaluations</label>
                                <p className="text-sm text-gray-500 mt-1">(Comments and prayer requests)</p>
                                <textarea
                                    id="evaluations"
                                    name="evaluations"
                                    rows={5}
                                    className="mt-2 px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                    value={report.evaluations || ""}
                                    onChange={handleEvChange}
                                />
                            </div>
                        </section>

                        <ReportSubmitBar
                            membersPresent={memberPeople.filter((p) => p.present).length}
                            membersTotal={memberPeople.length}
                            guestsPresent={guestPeople.filter((p) => p.present).length}
                            label={saveStatus === 'saving' ? 'Saving…' : 'Save Report'}
                            disabled={!formIsValid || saveStatus === 'saving'}
                            onSubmit={handleSubmit}
                        />
                    </form>
                </main>
            )}
        </div>
    );
};

AddReport.propTypes = {
    report: PropTypes.object,
    cellMembers: PropTypes.array,
    guests: PropTypes.array,
    getReportDetailsRequested: PropTypes.func,
    getReportsRequested: PropTypes.func,
    reportDetailsRequested: PropTypes.bool,
}

AddReport.defaultProps = {
    report: null,
    cellMembers: [],
    guests: [],
    reportDetailsRequested: false,
}

const mapStateToProps = (state) => ({
    report: state.report.report,
    cellMembers: state.report.cellMembers,
    guests: state.report.guests,
    saveStatus: state.report.saveStatus,
    saveError: state.report.saveError,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getReportDetailsRequested,
    getReportsRequested,
    submitNewReportRequested,
    updateAttendance,
    updateGuestAttendance,
    updateTestimonies,
    updateEvaluations,
    updateDate,
    addReportGuest,
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(AddReport);
